import React from 'react';
import { syncMetafields, saveMetafields, resetErrors } from '../../modules/shop'
import withRouter from 'with-router'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'

import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import ItemsList from '../../components/ItemsList'
import LoadingIndicator from '../../components/LoadingIndicator';
import SolutionsArticleLink from '../../components/SolutionsArticleLink';
import { withTranslation } from 'react-i18next';

const styles = {
  paper: {
    width: '100%',
    marginTop: '20px',
    position: 'relative',
  },
};

// the id the fields hang off, and what onSave reads its pending edits by
const ITEM_ID = 'metafields';

const buildFields = (metafields) => metafields.map(item => {
  const htmlContent = item.source.match(/^<p.*>(.*)<\/p>$/gim);

  return {
    id: item.id,
    multiline: true,
    rows: 5,
    type: htmlContent ? 'richtext' : 'text',
    source: item.source,
    target: item.target || '',
    label: item.namespace + ' -> ' + item.key
  };
});

// built from props, not only on a sync response - lets the previous set stay on screen
// while the fresh sync runs, instead of the page emptying first
const buildItem = (props) => ({
  id: ITEM_ID,
  title: props.t('pages.shop_metafields.title'),
  fields: buildFields(props.shop.metafields),
});

/**
 * Shop metafields. Its own page rather than a Shop subsection: METAFIELD GIDs carry no owner,
 * so there is no usable translatableResources listing and this keeps its own paginated sync.
 */
class ShopMetafields extends React.Component {

  constructor(props) {
    super(props);

    this.state = {
      data: [buildItem(props)],
    }
  }

  componentDidMount() {
    this.sync();
  }

  componentDidUpdate(prevProps) {
    if(this.props.editLanguage !== prevProps.editLanguage) {
      this.sync();
    }
  }

  componentWillReceiveProps(newProps) {
    if((this.props.shop.sync_shop_metafields_pending === true && newProps.shop.sync_shop_metafields_pending === false)
      || (this.props.shop.save_shop_metafields_pending === true && newProps.shop.save_shop_metafields_pending === false)) {
      this.setState({ data: [buildItem(newProps)] });
    }
  }

  // every visit, not just the first: shop metafields are edited outside langify and the
  // store would otherwise serve whatever the session synced earlier
  sync = () => {
    this.props.syncMetafields(this.props.editLanguage);
  }

  onSave = (items, fields) => {
    const item = items.find(x => x.id === ITEM_ID);
    if(!item) {
      return;
    }

    const usedFields = [];
    item.fields.forEach(field => {
      if(fields[`${ITEM_ID}-${field.id}`] || fields[`${ITEM_ID}-${field.id}`] === "") {
        usedFields.push({
          id: field.id,
          value: field.target
        })
      }
    })

    this.props.saveMetafields(this.props.editLanguage, usedFields);
  }

  isPending = () => this.props.shop.sync_shop_metafields_pending || this.props.shop.save_shop_metafields_pending

  render() {
    const { t } = this.props;

    return <React.Fragment>
      <Typography variant="h4" gutterBottom component="h2">
        {t('pages.shop_metafields.title')}

        <SolutionsArticleLink
          article="metafieldTranslations"
          subject={t('pages.shop_metafields.title')}
          placement="heading"
        />
      </Typography>
      <Divider className="divider-white" />
      <Paper sx={styles.paper} elevation={1}>
        <ItemsList
          type="shop_metafields"
          showOnlyFieldsList={true}
          clearEditsOnSave={true}
          // one wrapping item, so there is nothing to sort at item level - the fields do sort
          hideSorting={true}
          sortFields={true}
          // the count row above the fields too - hundreds of metafields, no item level to tell
          pagination={true}
          paginate={false}
          // a namespace -> key label is the only thing that tells two metafields apart
          searchFieldLabels={true}
          onSave={this.onSave}
          isPending={this.isPending()}
          isSaving={this.props.shop.save_shop_metafields_pending}
          data={this.state.data}
          forceUpdate={this.state.data[0].fields.length}
          allowAutoTranslation={true}
          allowFieldFiltering={true}
          error={this.props.shop.error}
        />
      </Paper>
      <LoadingIndicator
        pending={this.isPending()}
        progress={this.props.shop.progress}
        loadingType={this.props.shop.save_shop_metafields_pending ? 'saving' : 'loading'}
        error={this.props.shop.error}
        onErrorCancel={this.props.resetErrors}
      />
    </React.Fragment>
  }

}

const mapStateToProps = ({ shop }) => ({
  shop: shop,
  editLanguage: shop.shop.edit_language
})

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      syncMetafields,
      saveMetafields,
      resetErrors
    },
    dispatch
  )

export default withRouter(connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(ShopMetafields)))
